import { AssetReoModelV2, LiabilityModelV2 } from './models';
import { AssetReoModel, LiabilityModel, RealEstateOwnedStatuses } from '../../../models';

const DEFAULT_OMITTED_TOOLTIP = `Amount was "Omitted" from the liability record`;
const DEFAULT_PAID_OFF_TOOLTIP = `Amount was marked "Paid Off" on the liability record`;

export class ReoV2Utils {
  static mapReoToReoV2(reo: AssetReoModel, liabilities: LiabilityModel[] = []): AssetReoModelV2 {
    return {
      ...reo,
      marketValue: reo.marketValue || 0,
      netRentalIncome: reo.netRentalIncome || 0,
      piti: reo.piti || 0,
      linkedLiabilities: liabilities.filter((liability) => reo.linkedLiabilityIds.some((id) => id === liability.liabilityId)),
      _NRI: (reo.isNetRentalIncomeOverridden ? reo.netRentalIncomeOverrideValue : reo.netRentalIncome) || 0,
    };
  }

  static getReoPreCalculatedValues(reo: AssetReoModelV2, flyoutMode: boolean = false): AssetReoModelV2 {
    // REO Tooltips
    const propertySoldTooltip = ReoV2Utils.getSoldStrikeoutTooltip(reo);
    const reoOmittedTooltip = ReoV2Utils.getREOOmittedTooltip(reo);
    const reoPaidOffTooltip = ReoV2Utils.getREOPaidOffTooltip(reo);
    const nriTooltip = ReoV2Utils.getNRIStrikeoutTooltip(reo);
    const totalPaymentNRI = nriTooltip ? nriTooltip.replace('@column', 'Total Payment') : nriTooltip;

    // Strike-Out Properties
    const _hideAndIgnoreNRI = !nriTooltip; //Not used in HTML but in code instead (Totals Calculations)
    const marketValueStrikeOut = !!propertySoldTooltip;
    const totalPaymentStrikeOut = !!(propertySoldTooltip || nriTooltip || reoOmittedTooltip || reoPaidOffTooltip);

    // Tooltip Properties
    const marketValueTooltip = ReoV2Utils.tooltipArrayToHtml(propertySoldTooltip);
    const totalPaymentTooltip = flyoutMode
      ? ReoV2Utils.tooltipArrayToHtml(totalPaymentNRI) // Flyout has less tooltips for "Total Payment".
      : ReoV2Utils.tooltipArrayToHtml(propertySoldTooltip, reoOmittedTooltip, reoPaidOffTooltip, totalPaymentNRI);

    // Total Payment
    const totalPayment = reo.isPitiOverridden ? reo.pitiOverrideValue : ReoV2Utils.getReoTotalPayment(reo);
    const linkedLiabilities = (reo.linkedLiabilities ?? []).map((lien) => {
      return ReoV2Utils.getLiabilityPreCalculatedValues(lien, reo);
    });

    return {
      ...reo,
      linkedLiabilities,
      marketValueStrikeOut,
      totalPaymentStrikeOut,
      _hideAndIgnoreNRI,
      marketValueTooltip,
      totalPaymentTooltip,
      totalPayment,
    } as AssetReoModelV2;
  }

  static getLiabilityPreCalculatedValues(lien: LiabilityModelV2, reo: AssetReoModelV2): LiabilityModelV2 {
    const nriTooltip = ReoV2Utils.getNRIStrikeoutTooltip(reo);
    const propertySoldTooltip = ReoV2Utils.getSoldStrikeoutTooltip(reo);
    const omittedTooltip = ReoV2Utils.getLienOmittedTooltip(lien);
    const paidOffTooltip = ReoV2Utils.getLienPaidOffTooltip(lien);
    const pAndINRITooltip = nriTooltip ? nriTooltip.replace('@column', 'P&I') : nriTooltip;

    const defaultLienTooltips = [propertySoldTooltip, paidOffTooltip, omittedTooltip];
    const defaultLienStrikeOut = !!(propertySoldTooltip || paidOffTooltip || omittedTooltip);

    return {
      ...lien,
      liabilityTooltip: ReoV2Utils.tooltipArrayToHtml(...defaultLienTooltips),
      lienPositionTooltip: ReoV2Utils.tooltipArrayToHtml(...defaultLienTooltips),
      unpaidBalanceTooltip: ReoV2Utils.tooltipArrayToHtml(...defaultLienTooltips),
      pAndITooltip: ReoV2Utils.tooltipArrayToHtml(...defaultLienTooltips, pAndINRITooltip),
      liabilityStrikeOut: !!defaultLienStrikeOut,
      lienPositionStrikeOut: !!defaultLienStrikeOut,
      unpaidBalanceStrikeOut: !!defaultLienStrikeOut,
      pAndIStrikeOut: !!(defaultLienStrikeOut || pAndINRITooltip),
    } as LiabilityModelV2;
  }

  /**
   * Get the total payment for an REO
   * @param reo
   */
  static getReoTotalPayment(reo: AssetReoModelV2): number {
    const moPayments: number =
      reo.linkedLiabilities
        ?.filter((lien) => !lien.omitted && !lien.toBePaidOff)
        .reduce((val, lien) => val + Math.abs(lien.monthlyPayment || 0), 0) || 0;

    return moPayments + Math.abs(reo.monthlyInsTaxHoa || 0);
  }

  static getSoldStrikeoutTooltip(reo: AssetReoModelV2): string | undefined {
    if (!this.reoIsSold(reo)) {
      return 'Amount not included in Totals because Property Status is "Sold"';
    }

    return undefined;
  }
  static getNRIStrikeoutTooltip(reo: AssetReoModelV2): string | undefined {
    if (!this.reoHasNRI(reo)) {
      return undefined;
    }

    const defaultNRITooltip = '@column not included in Totals due to @option Net Rental Income for this property';

    if (reo._NRI > 0) {
      return defaultNRITooltip.replace('@option', 'positive');
    }
    if (reo._NRI < 0) {
      return defaultNRITooltip.replace('@option', 'negative');
    }
    return defaultNRITooltip.replace('@option', 'breakeven');
  }

  static getREOOmittedTooltip(reo: AssetReoModelV2): string | undefined {
    const hasSomeOmittedLiabilities = reo.linkedLiabilities?.length > 0 && reo.linkedLiabilities.every(this.isLiabilityOmitted);

    if (hasSomeOmittedLiabilities) {
      return DEFAULT_OMITTED_TOOLTIP;
    }

    return undefined;
  }

  static getREOPaidOffTooltip(reo: AssetReoModelV2): string | undefined {
    const hasSomePaidOffLiabilities = reo.linkedLiabilities?.length > 0 && reo.linkedLiabilities.every(this.isLiabilityPaidOff);

    if (hasSomePaidOffLiabilities) {
      return DEFAULT_PAID_OFF_TOOLTIP;
    }

    return undefined;
  }

  static getLienOmittedTooltip(lien: LiabilityModelV2): string | undefined {
    if (this.isLiabilityOmitted(lien)) {
      return DEFAULT_OMITTED_TOOLTIP;
    }

    return undefined;
  }

  static getLienPaidOffTooltip(lien: LiabilityModelV2): string | undefined {
    if (this.isLiabilityPaidOff(lien)) {
      return DEFAULT_PAID_OFF_TOOLTIP;
    }

    return undefined;
  }

  /**
   * Convert a list of strings to HTML used in tooltips
   * @param tooltips
   */
  static tooltipArrayToHtml(...tooltips: (string | undefined)[]): string | undefined {
    tooltips = tooltips.filter((tooltip) => tooltip);

    if (tooltips.length === 0) {
      return undefined;
    }

    if (tooltips.length === 1) {
      return tooltips[0];
    }

    return `<ul class="ps-3 m-0">${tooltips.map((tip) => `<li>${tip}</li>`).join('')}</ul>`;
  }

  /**
   * Check when REO is with status Sold
   * @param reo
   */
  static reoIsSold(reo: AssetReoModelV2) {
    // Ignore sold properties
    return reo.realEstateOwnedStatus !== RealEstateOwnedStatuses.Sold;
  }

  /**
   * Check when liability is marked as Omitted
   * @param liabilityModel
   */
  static isLiabilityOmitted(liabilityModel: LiabilityModel) {
    return !!liabilityModel.omitted;
  }

  /**
   * heck when liability is marked as Paid-Off
   * @param liabilityModel
   */
  static isLiabilityPaidOff(liabilityModel: LiabilityModel) {
    return !!liabilityModel.toBePaidOff;
  }

  /**
   * Determine when an REO has Net Rental Income
   * @param reo
   */
  static reoHasNRI(reo: AssetReoModelV2) {
    return reo.realEstateOwnedStatus === RealEstateOwnedStatuses.Rental;
  }

  /**
   * Get a total when the values are the sum of linked liabilities properties
   * @param liabilityKey Key on the liability model to sum
   * @param ignoreKey Key on the liability model that will determine when a value should be added to the sum
   * @param liabilities List of liabilities to calculate a total from
   * @private
   */
  static getTotalFromLiabilitiesByKey(
    liabilityKey: keyof LiabilityModelV2,
    ignoreKey: keyof LiabilityModelV2,
    liabilities: LiabilityModelV2[],
  ): number {
    // Calculate the total
    return liabilities
      .filter((lien) => !lien[ignoreKey])
      .reduce((runningTotalLiabilities, currentLiability) => {
        return runningTotalLiabilities + ((currentLiability[liabilityKey] as number) || 0);
      }, 0);
  }
}
